import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import config from "../../config";

export default function DeployButton() {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [unitName, setUnitName] = useState("");
  const [hovered, setHovered] = useState(false);
  const [pipelineId, setPipelineId] = useState(null);
  const [pipelineStatus, setPipelineStatus] = useState("");
  const [progress, setProgress] = useState(0);
  const [debugLogs, setDebugLogs] = useState([]);

  const pollingRef = useRef(null);
  const applyingRef = useRef(false);

  const statusBaseProgress = {
    created: 5,
    pending: 15,
    running: 60,
    success: 85,
    failed: 100,
    canceled: 100,
    skipped: 100,
  };

  const addDebugLog = (message, payload) => {
    const time = new Date().toLocaleTimeString();
    const line = payload
      ? `[${time}] ${message} ${JSON.stringify(payload)}`
      : `[${time}] ${message}`;

    console.log("[DeployButton]", message, payload || "");

    setDebugLogs((prev) => {
      const next = [...prev, line];
      return next.slice(-20);
    });
  };

  const clearPolling = () => {
    if (pollingRef.current) {
      addDebugLog("clear polling");
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const extractErrorMessage = (e) => {
    return (
      e?.response?.data?.error ||
      e?.response?.data?.message ||
      e?.message ||
      String(e)
    );
  };

  const startLocalUpdate = async () => {
    addDebugLog("startLocalUpdate entered");

    if (applyingRef.current) {
      addDebugLog("startLocalUpdate skipped because already applying");
      return;
    }

    applyingRef.current = true;
    setMsg("릴리스 파일 생성이 완료되었습니다.\n현재 장비에 업데이트를 적용하는 중입니다...");
    setProgress(90);

    const url = `${config.backendUrl}/api/deploy/update-latest`;
    addDebugLog("POST update-latest request", { url });

    try {
      const response = await axios.post(
        url,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 20000,
        }
      );

      addDebugLog("POST update-latest response", response.data);

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(data.error || "로컬 업데이트 시작 실패");
      }

      setUnitName(data.unit || "");
      setProgress(100);
      setMsg(
        "업데이트 작업이 시작되었습니다.\n서버가 재시작될 수 있으니 잠시 후 화면을 새로고침해 주세요."
      );
    } catch (e) {
      const errorMessage = extractErrorMessage(e);

      addDebugLog("POST update-latest error", {
        message: errorMessage,
        status: e?.response?.status,
        data: e?.response?.data,
      });

      /*
       * update-latest는 업데이트 중 myflaskapp.service를 재시작하므로
       * 연결이 끊기면서 Network Error 또는 timeout이 날 수 있다.
       * 이 경우 실제 업데이트는 systemd 작업에서 계속 진행될 수 있다.
       */
      if (
        errorMessage.includes("Network Error") ||
        errorMessage.includes("timeout") ||
        errorMessage.includes("ECONNABORTED") ||
        errorMessage.includes("Empty reply")
      ) {
        setProgress(100);
        setMsg(
          "업데이트 작업이 시작되었을 수 있습니다.\n서버가 재시작되는 동안 연결이 끊겼습니다. 잠시 후 화면을 새로고침해 주세요."
        );
      } else {
        setMsg(`실패: ${errorMessage}`);
      }
    } finally {
      addDebugLog("startLocalUpdate finished");
      setLoading(false);
      applyingRef.current = false;
    }
  };

  const fetchPipelineStatus = async (targetPipelineId) => {
    const url = `${config.backendUrl}/api/deploy/status`;

    addDebugLog("GET pipeline status request", {
      url,
      pipeline_id: targetPipelineId,
    });

    try {
      const response = await axios.get(url, {
        params: {
          pipeline_id: targetPipelineId,
        },
        timeout: 10000,
      });

      addDebugLog("GET pipeline status response", response.data);

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(data.error || "파이프라인 상태 조회 실패");
      }

      const nextStatus =
        data.status ||
        data.pipeline_status ||
        data?.pipeline?.status ||
        "";

      setPipelineStatus(nextStatus);
      setProgress((prev) =>
        Math.max(prev, statusBaseProgress[nextStatus] ?? prev)
      );

      if (nextStatus === "success") {
        addDebugLog("pipeline success, start local update", {
          pipeline_id: targetPipelineId,
        });

        clearPolling();
        await startLocalUpdate();
        return;
      }

      if (["failed", "canceled", "skipped"].includes(nextStatus)) {
        addDebugLog("pipeline ended with failure status", {
          pipeline_id: targetPipelineId,
          status: nextStatus,
        });

        clearPolling();
        setLoading(false);
        setProgress(100);
        setMsg(`실패: GitLab 릴리스 생성이 ${nextStatus} 상태로 종료되었습니다.`);
        return;
      }

      setMsg(
        `GitLab에서 업데이트 파일을 생성 중입니다.\n현재 상태: ${
          nextStatus || "확인 중"
        }`
      );
    } catch (e) {
      const errorMessage = extractErrorMessage(e);

      addDebugLog("GET pipeline status error", {
        message: errorMessage,
        status: e?.response?.status,
        data: e?.response?.data,
      });

      clearPolling();
      setLoading(false);
      setMsg(`실패: ${errorMessage}`);
    }
  };

  const onClick = async () => {
    addDebugLog("button clicked");

    const confirmed = window.confirm(
      "최신 버전으로 업데이트를 시작하시겠습니까?\n\n1. GitLab에서 최신 업데이트 파일을 생성합니다.\n2. 생성이 끝나면 현재 장비에 적용합니다.\n\n업데이트 중에는 서버가 재시작되며 화면이 잠시 끊길 수 있습니다."
    );

    addDebugLog("confirm result", { confirmed });

    if (!confirmed) return;

    clearPolling();

    setLoading(true);
    setMsg("GitLab 업데이트 파일 생성을 요청하는 중입니다...");
    setUnitName("");
    setPipelineId(null);
    setPipelineStatus("");
    setProgress(0);
    setDebugLogs([]);
    applyingRef.current = false;

    const url = `${config.backendUrl}/api/deploy/trigger`;

    addDebugLog("POST trigger request", {
      url,
      backendUrl: config.backendUrl,
    });

    try {
      const response = await axios.post(
        url,
        {
          reason: "manual-update-button",
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 15000,
        }
      );

      addDebugLog("POST trigger response", response.data);

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(data.error || "GitLab 파이프라인 실행 실패");
      }

      const nextPipelineId =
        data.pipeline_id ||
        data.id ||
        data?.pipeline?.id ||
        data?.result?.id;

      if (!nextPipelineId) {
        throw new Error("pipeline_id를 받지 못했습니다.");
      }

      const nextStatus =
        data.status ||
        data.pipeline_status ||
        data?.pipeline?.status ||
        "created";

      setPipelineId(nextPipelineId);
      setPipelineStatus(nextStatus);
      setProgress(statusBaseProgress[nextStatus] ?? 5);
      setMsg(
        `GitLab 업데이트 파일 생성이 시작되었습니다.\nPipeline ID: ${nextPipelineId}`
      );

      addDebugLog("pipeline created", {
        pipeline_id: nextPipelineId,
        status: nextStatus,
      });

      await fetchPipelineStatus(nextPipelineId);

      pollingRef.current = setInterval(() => {
        fetchPipelineStatus(nextPipelineId);
      }, 5000);

      addDebugLog("polling started", {
        pipeline_id: nextPipelineId,
        interval_ms: 5000,
      });
    } catch (e) {
      const errorMessage = extractErrorMessage(e);

      addDebugLog("POST trigger error", {
        message: errorMessage,
        status: e?.response?.status,
        data: e?.response?.data,
      });

      clearPolling();
      setLoading(false);
      setMsg(`실패: ${errorMessage}`);
    }
  };

  useEffect(() => {
    addDebugLog("component mounted", {
      backendUrl: config.backendUrl,
    });

    return () => {
      clearPolling();
    };
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <button
        onClick={onClick}
        disabled={loading}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          width: 200,
          height: 40,
          borderRadius: 6,
          border: hovered && !loading ? "1px solid #94a3b8" : "1px solid #cbd5e1",
          background: loading ? "#e2e8f0" : hovered ? "#e2e8f0" : "#f8fafc",
          color: "#0f172a",
          fontWeight: 600,
          cursor: loading ? "not-allowed" : "pointer",
        }}
      >
        {loading ? "업데이트 진행 중..." : "업데이트 시작"}
      </button>

      {msg && (
        <div
          style={{
            fontSize: 13,
            color: msg.startsWith("실패") ? "#b91c1c" : "#0f172a",
            whiteSpace: "pre-line",
            lineHeight: 1.5,
          }}
        >
          {msg}
        </div>
      )}

      {loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ fontSize: 13, color: "#0f172a" }}>
            진행상태: {pipelineStatus || "준비 중"}
          </div>

          <div
            style={{
              position: "relative",
              height: 18,
              borderRadius: 6,
              background: "#e2e8f0",
              overflow: "hidden",
              border: "1px solid #cbd5e1",
              width: 260,
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${Math.round(progress)}%`,
                background:
                  pipelineStatus === "failed" || pipelineStatus === "canceled"
                    ? "#ef4444"
                    : "#3b82f6",
                transition: "width 0.5s ease",
              }}
            />

            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                color: "#0f172a",
                fontWeight: 600,
              }}
            >
              {Math.round(progress)}%
            </div>
          </div>
        </div>
      )}

      {pipelineId && (
        <div
          style={{
            fontSize: 12,
            color: "#475569",
            wordBreak: "break-all",
          }}
        >
          Pipeline ID: {pipelineId}
        </div>
      )}

      {unitName && (
        <div
          style={{
            fontSize: 12,
            color: "#475569",
            wordBreak: "break-all",
          }}
        >
          작업 ID: {unitName}
        </div>
      )}

      {unitName && (
        <div
          style={{
            fontSize: 12,
            color: "#64748b",
            lineHeight: 1.5,
          }}
        >
          장비 터미널에서 아래 명령으로 업데이트 로그를 확인할 수 있습니다.
          <br />
          <code>{`sudo journalctl -u ${unitName} --no-pager`}</code>
        </div>
      )}

      <details
        style={{
          marginTop: 8,
          width: 520,
          maxWidth: "100%",
          fontSize: 12,
          color: "#334155",
        }}
      >
        <summary style={{ cursor: "pointer", fontWeight: 600 }}>
          업데이트 디버그 로그
        </summary>

        <pre
          style={{
            marginTop: 8,
            padding: 8,
            background: "#f8fafc",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
            maxHeight: 240,
            overflow: "auto",
          }}
        >
          {debugLogs.length > 0 ? debugLogs.join("\n") : "아직 로그 없음"}
        </pre>
      </details>
    </div>
  );
}